/*
=========================================================
* RingFoods Hero Section
=========================================================
*/

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";

// @mui icons
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

// Material Kit 2 PRO React components
import MKBox from "components/MKBox";
import MKButton from "components/MKButton";
import MKTypography from "components/MKTypography";
import MKBadge from "components/MKBadge";

import { accentSuccess, accentInfo } from "pages/RingFoodsHomepage/theme";
import useSignupUrl from "hooks/useSignupUrl";

// Background images (Unsplash) - responsive sizes for mobile vs desktop
const bgImageMobile = "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=600&q=50&fm=webp";
const bgImageDesktop = "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1200&q=60&fm=webp";

function Hero() {
  const signupUrl = useSignupUrl();
  const trustBadges = [
    "Setup in 30 mins",
    "Not charged until trial ends",
    "Cancel anytime",
    "Plans from $100/mo",
  ];

  const teamChanges = [
    "No more phone anxiety during rush hour",
    "No more waking up to missed call voicemails",
    "No more staff absorbing customer abuse",
    "No more language barrier awkwardness",
    "No more choosing between the phone and the guest in front of you",
  ];

  return (
    <MKBox
      minHeight={{ xs: "auto", md: "100vh" }}
      width="100%"
      sx={{
        backgroundImage: {
          xs: `linear-gradient(rgba(0, 0, 0, 0.65), rgba(0, 0, 0, 0.65)), url(${bgImageMobile})`,
          md: `linear-gradient(rgba(0, 0, 0, 0.65), rgba(0, 0, 0, 0.65)), url(${bgImageDesktop})`,
        },
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: { md: "fixed", xs: "scroll" },
        display: "flex",
        alignItems: { xs: "flex-start", md: "center" },
        py: { xs: 8, md: 0 },
        position: "relative",
        overflow: "hidden",
      }}
    >

      <Container sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 6, md: 0 } }}>
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center" justifyContent="center">
          {/* Main Content */}
          <Grid item xs={12} lg={6}>
            <MKBox textAlign={{ xs: "center", lg: "left" }} sx={{ px: { xs: 0 } }}>
              {/* Badge */}
              <MKBadge
                variant="contained"
                color="info"
                badgeContent="AI Voice Agent for Restaurants"
                container
                sx={{ mb: 2 }}
              />

              {/* Main Headline */}
              <MKTypography
                variant="h1"
                color="white"
                sx={{
                  fontSize: { xs: "2rem", sm: "2.5rem", md: "3.25rem", lg: "3.75rem" },
                  fontWeight: 700,
                  lineHeight: { xs: 1.25, md: 1.2 },
                  mb: { xs: 1.5, md: 2 },
                  wordBreak: "break-word",
                }}
              >
                Save Your Staff from{" "}
                <MKTypography
                  component="span"
                  variant="h1"
                  sx={{
                    fontSize: "inherit",
                    background: `linear-gradient(45deg, ${accentSuccess}, ${accentInfo})`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    fontWeight: 700,
                  }}
                >
                  Phone Stress
                </MKTypography>
              </MKTypography>

              {/* Keyworded subhead.
                  The H1 stays emotional because it converts; this h2 is the
                  line that tells Google (and a first-time visitor) what the
                  product literally is. Before it existed the hero contained the
                  words "AI voice agent" only inside the badge above, so the
                  page's main copy never said what it sells. Real rendered
                  text, not a meta tag — that is the part that gets weighed. */}
              <MKTypography
                component="h2"
                variant="h4"
                color="white"
                fontWeight={400}
                sx={{ mb: { xs: 1.5, md: 2 }, opacity: 0.95, fontSize: { xs: "1.15rem", md: "1.35rem" }, lineHeight: 1.5 }}
              >
                RingFoods is the AI voice agent that answers your restaurant&apos;s phone 24/7
                &mdash; reservations, orders and questions, handled.
              </MKTypography>

              {/* Subheadline */}
              <MKTypography
                variant="body1"
                color="white"
                fontWeight={300}
                sx={{ mb: { xs: 2, md: 3 }, opacity: 0.88, fontSize: { xs: "1rem", md: "1.1rem" }, lineHeight: 1.6 }}
              >
                It handles every call &mdash; the rude ones, the 2am ones, the ones in languages
                your staff doesn&apos;t speak &mdash; so your team can focus on the people in front of them.
              </MKTypography>

              {/* CTA Buttons */}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: { xs: 3, md: 4 } }}>
                <MKButton
                  size="large"
                  variant="contained"
                  onClick={() => window.location.href = signupUrl}
                  sx={{
                    backgroundColor: "#FFFFFF !important",
                    color: "#1A1A1A !important",
                    px: { xs: 3, md: 4 },
                    py: { xs: 1.25, md: 1.5 },
                    fontSize: { xs: "1rem", md: "1.1rem" },
                    fontWeight: 600,
                    borderRadius: 2,
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "#F5F5F5 !important",
                      transform: "translateY(-2px)",
                      boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Start Free Trial
                </MKButton>

                <MKButton
                  size="large"
                  variant="outlined"
                  startIcon={<PlayCircleOutlineIcon />}
                  component="a"
                  href="/demo.html"
                  sx={{
                    borderColor: "#FFFFFF !important",
                    color: "#FFFFFF !important",
                    px: { xs: 3, md: 4 },
                    py: { xs: 1.25, md: 1.5 },
                    fontSize: { xs: "1rem", md: "1.1rem" },
                    fontWeight: 600,
                    borderRadius: 2,
                    "&:hover": {
                      borderColor: "#FFFFFF !important",
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  View Demo
                </MKButton>
              </Stack>

              {/* Trust Badges */}
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                justifyContent={{ xs: "center", lg: "flex-start" }}
                sx={{ rowGap: 1 }}
              >
                {trustBadges.map((badge, index) => (
                  <MKBadge
                    key={index}
                    badgeContent={badge}
                    variant="contained"
                    color="light"
                    size="sm"
                    sx={{ mb: 1 }}
                  />
                ))}
              </Stack>
            </MKBox>
          </Grid>

          {/* What Changes for Your Team */}
          <Grid item xs={12} lg={6}>
            <MKBox textAlign="center" sx={{ px: { xs: 0 }, mt: { xs: 2, md: 0 } }}>
              <MKBox
                sx={{
                  background: "rgba(255, 255, 255, 0.1)",
                  backdropFilter: "blur(20px)",
                  borderRadius: 3,
                  p: { xs: 2.5, md: 4 },
                  mb: { xs: 3, md: 4 },
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                }}
              >
                <MKTypography variant="h5" color="white" fontWeight={600} sx={{ mb: 3 }}>
                  What Changes for Your Team
                </MKTypography>

                <MKBox sx={{ textAlign: "left", maxWidth: 520, mx: "auto" }}>
                  {teamChanges.map((item, idx) => (
                    <MKBox key={idx} sx={{ display: "flex", alignItems: "flex-start", mb: 1.75 }}>
                      <CheckCircleIcon sx={{ color: accentSuccess, mr: 1.5, mt: "2px", fontSize: 20 }} />
                      <MKTypography variant="body1" color="white" sx={{ opacity: 0.95, lineHeight: 1.5, fontSize: { xs: "0.95rem", md: "1.05rem" } }}>
                        {item}
                      </MKTypography>
                    </MKBox>
                  ))}
                </MKBox>
              </MKBox>
            </MKBox>
          </Grid>
        </Grid>
      </Container>

    </MKBox>
  );
}

export default Hero;
